import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import { Product } from '~/lib/data-contract/salesforce';
import { PDPProductCard } from '~/shared/components/ProductCard/PDPProductCard';
import { USPs } from '~/shared/components/Summary/components/USPs';
import { useFrame } from '~/shared/utils';
import { useTranslation } from '~/shared/utils/translation';
import { ModuleContainer } from '~/templates/blocks/components/ModuleContainer';
import { MatchingProducts } from '~/features/product-details-page/components/MatchingProducts';
import { usePage } from '~/templates/pages';
import { useBasket } from '../basket';
import { ProductEvent } from '../productList/PLPModule.definitions';
import { useProduct } from './hooks/usePDPStore';
import { mapProductData } from './pdp-utils';
import { P50ProductDetailsPage, TVariantSummary } from './pdp.definitions';
import { useViewedProducts } from './hooks/useViewedProducts';
import { useUtils } from '~/shared/hooks/useUtils';
import { useWishlist } from '../commerce-api/hooks/wishlist/useWishList';
import { useErrorHandler } from '~/shared/utils/errorBoundary/hooks/useErrorHandler';
import { EventIdentifier } from '~/shared/hooks/useEvents';
import { _transformProductToDataLayer } from '~/shared/hooks/useEvents/helpers';
import { ProductDetailSections } from './components/ProductDetailSections';
import { useRouter } from 'next/router';

import { PDPAside, PDPContentWrapper, PDPMedia, PDPSummary } from './components';
import {
    toggleGallery,
    setSelectedImageIndex,
    useProductDetailsStore,
    getImages,
    resetSelectedAttributes,
    selectVariant,
} from './hooks/useProductDetailsStore';
import { StyledStoresInStockLink } from './styled';
import { getSummeryImage } from '~/utils/image-group-utils';
import { ContentContainer } from './styled';
type Props = {
    children?: ReactNode;
    variantSummary: TVariantSummary;
    yotpoReviewRef?: React.RefObject<HTMLDivElement>;
};

const ReservationDrawer = dynamic(() => import('./components/ReservationDrawer'), { ssr: false });

export const PDPLayout = ({ variantSummary, yotpoReviewRef }: Props) => {
    const { translate } = useTranslation();
    const { trackAndDisplayError: trackError } = useErrorHandler();
    const { addItemToBasket, toggleMiniBasket } = useBasket();
    const { convertPriceToLocale } = useUtils();
    const { handleFavourite, isFavorite } = useWishlist();
    const { data: frame } = useFrame();
    const router = useRouter();
    const page = usePage();
    const { product, isLoading, isFetching } = useProduct();
    const { isGalleryOpen, selectedImageIndex, variationAttributes, variant } =
        useProductDetailsStore();

    const [showReservationDrawer, setShowReservationDrawer] = useState(false);

    const { uniqueSellingPoints } = frame.market;
    const isFreightByCarrier = ['C', 'D', 'E', 'F', 'G'].includes(product?.c_itemFreightGroup);

    const storesWithStock = product?.c_storeIdsWithStock?.length ?? 0;

    const images = getImages();

    const image = product && getSummeryImage(product);

    const freightInfo = {
        text: frame.market.freightPolicyText,
        title: frame.market.freightPolicyTitle,
    };

    useViewedProducts(product);

    const handleBuyBtn = async (_identifier: EventIdentifier, data: ProductEvent) => {
        try {
            switch (data.type) {
                case 'addtocart': {
                    return await addItemToBasket(
                        // list ?? '',
                        data.productEventData,
                        data.payload,
                        data.quantity,
                    );
                }
                default:
                    return () => {
                        throw new Error('Missing handler with data');
                    };
            }
        } catch (error) {
            trackError(error);
        }
    };

    const toggleReservationDrawer = () => {
        setShowReservationDrawer(!showReservationDrawer);
    };

    /*
     * NOTE: When clicking product links on the minibasket we don't want to prematurely dismiss the minibasket to confuse the user (IMC-142) ...
     * instead the minibasket will disappear on the navigated product that user intended to see. So in PDP we need to toggle off the Minibasket if its open.
     */
    useEffect(() => {
        toggleMiniBasket(false);
    }, [router, toggleMiniBasket]);

    useEffect(() => {
        return () => {
            /**  When unmounting, make sure to clear selectedAttributes,
             * else the next product will inherent the selected values
             */
            resetSelectedAttributes();
        };
    }, []);

    const uspTags = useMemo(() => {
        const abc = [];

        const christmasPrice = product?.c_prices?.find(
            (price) => price.schema && price.promotype === 'christmasprice',
        )?.promotype;

        if (christmasPrice) abc.push(christmasPrice as string);

        return abc;
    }, [product]);

    return (
        <ModuleContainer spacingTop="none">
            <ContentContainer>
                <PDPContentWrapper
                    renderMedia={
                        <>
                            <PDPMedia
                                name={product?.name}
                                thumbnailUrl={image.link}
                                selectedImageIndex={selectedImageIndex}
                                asGallery={isGalleryOpen}
                                videos={product?.c_videoLinks}
                                images={images}
                                onClick={setSelectedImageIndex}
                                onToggleGallery={toggleGallery}
                            />
                            <MatchingProducts />
                        </>
                    }
                >
                    <PDPAside>
                        <PDPSummary hasData={!!product}>
                            {product ? (
                                <>
                                    <PDPProductCard
                                        {...mapProductData(
                                            product ?? ({} as Product),
                                            translate,
                                            convertPriceToLocale,
                                        )}
                                        yotpoReviewRef={yotpoReviewRef}
                                        isLoading={isLoading || isFetching}
                                        onFavourite={handleFavourite}
                                        isFavourite={isFavorite(product.id)}
                                        productEventData={_transformProductToDataLayer(
                                            product ?? ({} as Product),
                                        )}
                                        currentVariant={variant}
                                        onBuyBtn={handleBuyBtn}
                                        onSecondaryBtn={toggleReservationDrawer}
                                        storeInfo={undefined}
                                        images={images}
                                        videos={product.c_videoLinks}
                                        renderShopsLink={
                                            !!storesWithStock && (
                                                <StyledStoresInStockLink
                                                    variant="link"
                                                    size="large"
                                                    onClick={toggleReservationDrawer}
                                                >
                                                    {translate('pdp.onStockInStores', {
                                                        amount: storesWithStock,
                                                    })}
                                                </StyledStoresInStockLink>
                                            )
                                        }
                                        cust_isRemainder={product?.c_isRemainder}
                                        c_ItemSoleRepresentation={product.c_ItemSoleRepresentation}
                                        selectVariant={selectVariant}
                                        variationAttributes={variationAttributes}
                                        toggleGallery={(index) => {
                                            setSelectedImageIndex(index);
                                            toggleGallery();
                                        }}
                                        showFreightBanner={isFreightByCarrier}
                                        carrierPrice={product.c_minimumShippingCost}
                                        campaignLifeCycle={product?.c_campaignLifeCycle}
                                        variantSummary={variantSummary}
                                        yotpoAverageRating={product?.c_yotpoAverageRating}
                                        yotpoReviewCount={product?.c_yotpoReviewCount}
                                    />

                                    {uniqueSellingPoints && (
                                        <USPs usps={uniqueSellingPoints} tags={uspTags} />
                                    )}

                                    <ProductDetailSections
                                        page={page as P50ProductDetailsPage}
                                        freightInfo={freightInfo}
                                        variant={variant}
                                        product={product}
                                    />
                                </>
                            ) : (
                                <div style={{ minHeight: '100vh' }} />
                            )}
                        </PDPSummary>
                    </PDPAside>
                </PDPContentWrapper>
            </ContentContainer>

            {showReservationDrawer && (
                <ReservationDrawer
                    variant={variant}
                    showReservationDrawer={showReservationDrawer}
                    images={images}
                    setShowReservationDrawer={setShowReservationDrawer}
                    product={product}
                />
            )}
        </ModuleContainer>
    );
};
